import { KeyboardEvent } from "react";

import { t } from "i18next";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Close from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { MAX_NOTE_LENGTH } from "@/src/config/baanaConfig";
import {
  MileageUnit,
  NoteDTO,
  PreviousAuctionNoteDTO
} from "@/src/types/types";

import NotesList from "./NotesList";

export interface NoteModalContentProps {
  regNo?: string;
  notes?: NoteDTO[];
  historyGroups: PreviousAuctionNoteDTO[][];
  currentMileage?: number | null;
  mileageUnit?: MileageUnit;
  isNoteHistoryError: boolean;
  onRetryHistory: () => void;
  onDeleteNote: (note: NoteDTO) => void;
  noteText: string;
  onNoteTextChange: (value: string) => void;
  onAddNote: () => void;
  isSending: boolean;
  /** Renders the grabber bar and header for the mobile sheet. The desktop dialog's
   * header/close button are owned by ModalWrapper. */
  variant: "dialog" | "sheet";
  onClose: () => void;
}

const NoteModalContent = (props: NoteModalContentProps) => {
  const {
    regNo,
    notes,
    historyGroups,
    currentMileage,
    mileageUnit,
    isNoteHistoryError,
    onRetryHistory,
    onDeleteNote,
    noteText,
    onNoteTextChange,
    onAddNote,
    isSending,
    variant,
    onClose
  } = props;
  const theme = useTheme();
  const isSheet = variant === "sheet";

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey && variant === "dialog") {
      event.preventDefault();
      onAddNote();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0
      }}
    >
      {isSheet && (
        <Box
          sx={{
            width: 32,
            height: 4,
            borderRadius: "2px",
            backgroundColor: theme.palette.divider,
            mx: "auto",
            my: 1
          }}
        />
      )}

      {isSheet && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 3,
            py: 2,
            flexShrink: 0
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: theme.typography.fontWeightBold, mb: 0 }}
          >
            {t("dealer.auctionDetailsPage.notes")}
          </Typography>
          {regNo && (
            <Typography
              variant="h4"
              sx={{ color: theme.palette.text.secondary, ml: 1, mb: 0 }}
            >
              {regNo}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton
            id="addEditNoteModalCloseButton"
            aria-label={t("general.close")}
            onClick={onClose}
          >
            <Close />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 3,
          pt: isSheet ? 0 : 2,
          pb: 2
        }}
      >
        <NotesList
          variant="full"
          notes={notes}
          historyGroups={historyGroups}
          currentMileage={currentMileage}
          mileageUnit={mileageUnit}
          isNoteHistoryError={isNoteHistoryError}
          onRetryHistory={onRetryHistory}
          onDeleteNote={onDeleteNote}
        />
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          px: 3,
          py: 2,
          pb: isSheet ? "calc(env(safe-area-inset-bottom) + 16px)" : 2
        }}
      >
        <TextField
          autoFocus={variant === "dialog"}
          id="messageTextField"
          placeholder={t("dealer.auctionDetailsPage.addNoteModal.enterMessage")}
          multiline
          minRows={1}
          maxRows={5}
          fullWidth
          disabled={isSending}
          value={noteText}
          onChange={(e) => onNoteTextChange(e.target.value)}
          onKeyDown={handleComposerKeyDown}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2
            }
          }}
          slotProps={{
            htmlInput: { maxLength: MAX_NOTE_LENGTH }
          }}
        />
        <IconButton
          id="addNoteButton"
          aria-label={t("general.add") ?? ""}
          disabled={isSending || noteText.trim() === ""}
          onClick={onAddNote}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled
            }
          }}
        >
          {isSending ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <ArrowUpwardIcon />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default NoteModalContent;
